/**
 * Environment variable handling
 * @module environment
 */
import _isUndefined from 'lodash/isUndefined'

/**
 * Local runtime i.e. running on a developer's machine
 */
export const LOCAL = 'development'
/**
 * Production runtime i.e. not local
 */
const PRODUCTION = 'production'
export const PROD = PRODUCTION
/**
 * The NODE ENV
 */
const NODE_ENV = process.env.NODE_ENV || LOCAL
/**
 * When true the app is running inside a browser
 */
const { BROWSER } = process.env
/**
 * When truthy enable Server Side Rendering
 */
const { ENABLE_SSR } = process.env

/**
 * Get the enviroment i.e. NODE_ENV
 */
export function get () {
  return NODE_ENV
}

/**
 * Return true if we are running in a local environment
 * i.e. you are developing on your laptop. If NODE_ENV
 * is equal to 'development'.
 */
export function isLocal () {
  return get() === LOCAL
}

/**
 * Return true if NODE_ENV === PRODUCTION i.e. the app
 * is running in a state that would experienced by a user
 */
export function isProduction () {
  return get() === PRODUCTION
}

/**
 * Return boolean value of process.env.ENABLE_SSR to
 * determine if Server Side Rendering is enabled
 * @returns {Boolean} True when SSR is enabled
 */
export function getEnableSsr () {
  return _isUndefined(ENABLE_SSR) ? true : Boolean(Number(process.env.ENABLE_SSR))
}

/**
 * Checks if touch feature are available.
 * @see https://ctrlq.org/code/19616-detect-touch-screen-javascript
 */
export function isTouch () {
  if (BROWSER) {
    return (
      'ontouchstart' in window
      || navigator.MaxTouchPoints > 1
      || navigator.msMaxTouchPoints > 0
    )
  }
  return false
}

/**
 * Returns true for iPhone or iPad, browser agnostic.
 */
export function isIOS (options = {}) {
  const { global: g = global } = options
  if (g && g.navigator) {
    const { navigator } = g
    const ua = navigator.userAgent
    return /iPad/i.test(ua) || /iPhone/i.test(ua)
  }
  return false
}

/**
 * Returns true for Android Device but not Amazon, browser agnostic.
 */
export function isNonAmazonAndroid (options = {}) {
  const { global: g = global } = options
  if (g && g.navigator) {
    const { navigator } = g
    const ua = navigator.userAgent
    const androidTest = /android/i.test(ua)
    const silkTest = /silk/i.test(ua)
    return androidTest && !silkTest
  }
  return false
}

/**
 * Returns true for Android Device, silk browser.
 */
export function isAmazon (options = {}) {
  const { global: g = global } = options
  if (g && g.navigator) {
    const { navigator } = g
    const ua = navigator.userAgent
    const androidTest = /android/i.test(ua)
    const silkTest = /silk/i.test(ua)
    return androidTest && silkTest
  }
  return false
}

/**
 * Returns true for Safari on either iPhone or iPad.
 * Addresses the similiarity in UA string from Chrome on iOS.
 */
export function isMobileSafari (options = {}) {
  const { global: g = global } = options
  if (g && g.navigator) {
    const { navigator } = g
    const ua = navigator.userAgent
    const webkit = /WebKit/i.test(ua)
    return isIOS(options) && webkit && !ua.match(/CriOS/i)
  }
  return false
}
